// The Leaflet canvas for Live Fleet. Plain Leaflet driven by refs, the same way
// AllocationMap is, and the same OpenStreetMap tiles: no key, no metered cost.
import { useEffect, useRef } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import './allocationMap.css'
import './liveFleetMap.css'
import { LIVE_STATE_LABEL, hasPosition, statusLine, type LiveFleetRow } from '../../utils/liveFleet'

type Props = {
  rows: LiveFleetRow[]
  selectedVehicleId: string | null
  onSelectVehicle: (vehicleId: string | null) => void
}

// Sandton, head office, when there is nothing to fit to.
const FALLBACK: L.LatLngExpression = [-26.1076, 28.0567]

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function tooltipFor(r: LiveFleetRow): string {
  const lines = [`<strong>${escapeHtml(r.plate ?? 'No plate')}</strong> ${escapeHtml(LIVE_STATE_LABEL[r.live_state])}`]
  lines.push(escapeHtml(statusLine(r)))
  if (r.position_description) lines.push(escapeHtml(r.position_description))
  if (r.driver_names) lines.push(`Seated: ${escapeHtml(r.driver_names)}`)
  if (r.manager_name) lines.push(`Manager: ${escapeHtml(r.manager_name)}`)
  return lines.join('<br>')
}

function carIcon(r: LiveFleetRow, selected: boolean): L.DivIcon {
  // The arrow shows heading only while the car is moving; a parked car's last
  // bearing is noise.
  const arrow =
    r.live_state === 'moving' && typeof r.bearing === 'number'
      ? `<span class="fleetly-car__arrow" style="transform: rotate(${r.bearing}deg)"></span>`
      : ''
  const size = selected ? 30 : 24
  return L.divIcon({
    className: '',
    html: `<div class="fleetly-car fleetly-car--${r.live_state}${selected ? ' fleetly-car--selected' : ''}">${arrow}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  })
}

export function LiveFleetMap({ rows, selectedVehicleId, onSelectVehicle }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<L.Map | null>(null)
  const layerRef = useRef<L.LayerGroup | null>(null)
  // Fit once. After that the manager's pan and zoom is theirs, and a refresh
  // every 30 seconds must not snap the map back.
  const fittedRef = useRef(false)

  useEffect(() => {
    if (mapRef.current || !containerRef.current) return
    const map = L.map(containerRef.current, { center: FALLBACK, zoom: 10, scrollWheelZoom: true })
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map)
    layerRef.current = L.layerGroup().addTo(map)
    mapRef.current = map
    return () => {
      map.remove()
      mapRef.current = null
      layerRef.current = null
    }
  }, [])

  useEffect(() => {
    const map = mapRef.current
    const layer = layerRef.current
    if (!map || !layer) return
    layer.clearLayers()
    const placed = rows.filter(hasPosition)
    for (const r of placed) {
      const selected = r.vehicle_id === selectedVehicleId
      const marker = L.marker([r.latitude, r.longitude], {
        icon: carIcon(r, selected),
        title: r.plate ?? undefined,
        zIndexOffset: selected ? 1000 : r.live_state === 'moving' ? 500 : 0,
      })
      marker.bindTooltip(tooltipFor(r), { direction: 'top', offset: [0, -12] })
      marker.on('click', () => onSelectVehicle(r.vehicle_id))
      layer.addLayer(marker)
    }
    if (!fittedRef.current && placed.length > 0) {
      map.fitBounds(L.latLngBounds(placed.map((r) => [r.latitude, r.longitude] as [number, number])), {
        padding: [40, 40],
        maxZoom: 14,
      })
      fittedRef.current = true
    }
  }, [rows, selectedVehicleId, onSelectVehicle])

  // An explicit selection is the one thing that moves the view.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !selectedVehicleId) return
    const r = rows.find((x) => x.vehicle_id === selectedVehicleId)
    if (r && hasPosition(r)) map.setView([r.latitude, r.longitude], Math.max(map.getZoom(), 14))
    // Only on a change of selection, not on every refresh of the same car.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVehicleId])

  return <div ref={containerRef} className="fleetly-map" />
}
